import React, { useEffect, useState } from "react";
import Text from "@/components/ui/text";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import CounterBadge from "@/components/ui/counter-badge";
import SingleCheckbox from "@/components/ui/SingleCheckbox";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDelete: () => void;
  stageName: string;
  count: number;
  targetStageName: string;
  tPrefix: string;
}

const DeleteStageModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  handleDelete,
  stageName,
  count,
  targetStageName,
  tPrefix
}) => {
  const { t } = useTranslation();

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsCheckboxChecked(false);
      setShowError(false);
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[35rem] [&_[data-test='close-modal']]:w-8 [&_[data-test='close-modal']]:h-8 [&_[data-test='close-modal']]:scale-100"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t(`${tPrefix}.title`, { stageName })}
        </Text>
      }
      content={
        <div className="flex flex-col gap-6 h-fit w-[35rem] p-4">
          <div className="flex flex-col gap-0.5">
            <Text as="div">
              {t(`${tPrefix}.content1`, {
                stageName
              })}
            </Text>
            <Text>{t(`${tPrefix}.content2`)}</Text>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex gap-[6px] items-center">
              <Text>{t(`${tPrefix}.content3`)}</Text>
              <CounterBadge count={count} />
              <Text>{t(`${tPrefix}.content4`)}</Text>
            </div>
            <Text>
              {t(`${tPrefix}.content5`, {
                targetStageName
              })}
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <SingleCheckbox
              id="permanentlyDeleteStage"
              label={t(`${tPrefix}.checkbox`)}
              isChecked={isCheckboxChecked}
              onCheckedChange={(checked: boolean) => {
                setIsCheckboxChecked(checked);
                if (checked && showError) {
                  setShowError(false);
                }
              }}
            />
            {showError && (
              <Text variant="smBody" className="text-error ml-2">
                {t(`${tPrefix}.checkboxError`)}
              </Text>
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            data-test="cancel"
            variant="secondary"
            onClick={() => setIsOpen(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="delete"
            destructive
            onClick={() => {
              if (!isCheckboxChecked) {
                setShowError(true);
                return;
              }
              handleDelete();
            }}
          >
            {t(`${tPrefix}.deleteStage`, {
              stageName
            })}
          </Button>
        </div>
      }
      hideDivider
    />
  );
};

export default DeleteStageModal;
